import React, { createContext, useContext, useEffect, useState } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import Icon from '../UIElements/Icon';
import { readSession, sessionKey, writeSession } from '../../util/session-storage';
import './BackNavigation.css';

const BackContext = createContext({ canGoBack: false });
const urlOf = (location) => location.pathname + location.search + location.hash;
const entryOf = (location) => ({ key: location.key || 'initial', url: urlOf(location) });
const validEntry = (entry) =>
  entry &&
  typeof entry.key === 'string' &&
  typeof entry.url === 'string' &&
  /^\/(?!\/)/.test(entry.url);

// Track only entries made inside this account's workspace. history.length also
// includes external websites and therefore cannot safely decide where Back goes.
export const NavigationMemory = ({ scope, children }) => {
  const history = useHistory();
  const key = sessionKey(scope, 'navigation');
  const [journal, setJournal] = useState(null);
  useEffect(() => {
    const saved = readSession(key, null);
    const entries =
      Array.isArray(saved?.entries) && saved.entries.every(validEntry) ? saved.entries : [];
    const current = entryOf(history.location);
    let index = entries.findIndex(
      (entry) => entry.key === current.key && entry.url === current.url
    );
    if (index < 0 && history.location.pathname === '/' && validEntry(entries[saved?.index])) {
      const last = entries[saved.index];
      // A new entry/tab history starts here; do not fabricate a browser back entry.
      history.replace(last.url);
    }
    let state =
      index >= 0 ? { entries, index } : { entries: [entryOf(history.location)], index: 0 };
    setJournal(state);
    writeSession(key, state);
    return history.listen((location, action) => {
      const entry = entryOf(location);
      if (action === 'PUSH') {
        const next = [...state.entries.slice(0, state.index + 1), entry].slice(-100);
        state = { entries: next, index: next.length - 1 };
      } else if (action === 'REPLACE') {
        const next = [...state.entries];
        next[state.index] = entry;
        state = { entries: next, index: state.index };
      } else {
        index = state.entries.findIndex((item) => item.key === entry.key && item.url === entry.url);
        state = index >= 0 ? { ...state, index } : { entries: [entry], index: 0 };
      }
      writeSession(key, state);
      setJournal(state);
    });
  }, [history, key]);
  if (!journal) return null;
  return (
    <BackContext.Provider value={{ canGoBack: journal.index > 0 }}>{children}</BackContext.Provider>
  );
};

export const BackNavigation = ({ doctorId }) => {
  const history = useHistory();
  const location = useLocation();
  const { canGoBack } = useContext(BackContext);
  const parent =
    location.pathname === '/care/team'
      ? '/care'
      : location.pathname.startsWith('/care/patients/')
        ? '/care/inbox'
        : doctorId && /\/(prescribe|reports|preview)(\/|$)/.test(location.pathname)
          ? `/${doctorId}/search`
          : '/';
  const disabled = !canGoBack && location.pathname === '/';
  return (
    <div className="back-navigation">
      <button
        type="button"
        disabled={disabled}
        onClick={() => (canGoBack ? history.goBack() : history.replace(parent))}
        aria-label="Back to previous screen"
      >
        <Icon name="back" size={17} />
        Back
      </button>
    </div>
  );
};

export const RouteContent = ({ children }) => {
  const location = useLocation();
  return (
    <div className="route-content" key={location.pathname}>
      {children}
    </div>
  );
};
